import { AxiosResponse } from '@/interface';
import { ActionContext, ActionTree } from 'vuex';
import { RootState } from '@/interface/store/';
import * as s_Interface from '@/interface/store/manageGroups';
import * as API from '@/api/manageGroup';
import * as c_Interface from '@/interface/components/manageGroups';
import { VendorForm, AddressForm, GrantForm, ManufacturerForm } from '@/interface/components';

export const actions: ActionTree<s_Interface.ManageGroup, RootState> = {
  async getMe({ commit }: ActionContext<s_Interface.ManageGroup, RootState>, groupId: string) {
    try {
      const { data }: AxiosResponse = await API.getMe(groupId);
      await commit('SET_ME', data);
      await commit('SET_LAST_GROUP_ID', data.fkUser.lastGroupId, { root: true });
    } catch (e) {
      throw e;
    }
  },
  async getGroups({ commit }: ActionContext<s_Interface.ManageGroup, RootState>) {
    try {
      const { data }: AxiosResponse = await API.getGroups();
      commit('GET_GROUPS', data);
    } catch (e) {
      throw e;
    }
  },
  async createLab({ commit }, payload: c_Interface.CreateGroupPayload) {
    try {
      const { data }: AxiosResponse = await API.setGroups(payload);
    } catch (e) {
      throw e;
    }
  },
  async deleteGroup({ commit }, { groupId }: { groupId: string }) {
    try {
      const { data }: AxiosResponse = await API.deleteGroups(groupId);
    } catch (e) {
      throw e;
    }
  },
  async getMembers({ commit }: ActionContext<s_Interface.ManageGroup, RootState>, groupId: string) {
    try {
      const { data }: AxiosResponse = await API.getMembers(groupId);
      commit('GET_MEMBERS', data);
    } catch (e) {
      throw e;
    }
  },
  async reminders(
    { commit },
    {
      groupId,
      memberId,
      payload,
    }: { groupId: string; memberId: string; payload: c_Interface.ReminderPayload }
  ) {
    try {
      const { data }: AxiosResponse = await API.reminders(groupId, memberId, payload);
    } catch (e) {
      throw e;
    }
  },
  async setMembers(
    { commit },
    { groupId, payload }: { groupId: string; payload: Array<c_Interface.Member> }
  ) {
    try {
      const { data }: AxiosResponse = await API.setMembers(groupId, payload);
    } catch (e) {
      throw e;
    }
  },
  async updateMembers(
    { commit },
    {
      groupId,
      memberId,
      memberFilter,
    }: { groupId: string; memberId: string; memberFilter: { isAdmin: boolean } }
  ) {
    try {
      const { data }: AxiosResponse = await API.updateMembers(groupId, memberId, memberFilter);
    } catch (e) {
      throw e;
    }
  },
  async deleteMembers({ commit }, { groupId, memberId }: { groupId: string; memberId: string }) {
    try {
      const { data }: AxiosResponse = await API.deleteMembers(groupId, memberId);
    } catch (e) {
      throw e;
    }
  },
  async getMainLocations({ commit }, groupId: string) {
    try {
      const { data }: AxiosResponse = await API.getMainLocations(groupId);
      commit('GET_MAIN_LOCATIONS', data);
    } catch (e) {
      throw e;
    }
  },
  async setMainLocation(
    { commit },
    { groupId, payload }: { groupId: string; payload: c_Interface.CreateLocationPayload }
  ) {
    try {
      const { data }: AxiosResponse = await API.setMainLocation(groupId, payload);
      return data;
    } catch (e) {
      throw e;
    }
  },
  async deleteMainLocation(
    { commit },
    { groupId, locationId }: { groupId: string; locationId: string }
  ) {
    try {
      const { data }: AxiosResponse = await API.deleteMainLocation(groupId, locationId);
    } catch (e) {
      throw e;
    }
  },
  async updateMainLocation(
    { commit },
    {
      groupId,
      locationId,
      payload,
    }: { groupId: string; locationId: string; payload: c_Interface.CreateLocationPayload }
  ) {
    try {
      const { data }: AxiosResponse = await API.updateMainLocation(groupId, locationId, payload);
    } catch (e) {
      throw e;
    }
  },
  async getSubLocations(
    { commit },
    { groupId, locationId }: { groupId: string; locationId: string }
  ) {
    try {
      const { data }: AxiosResponse = await API.getSubLocations(groupId, locationId);
      commit('GET_SUB_LOCATIONS', data);
    } catch (e) {
      throw e;
    }
  },
  async getSubLocation(
    { commit },
    { groupId, locationId, subId }: { groupId: string; locationId: string; subId: string }
  ) {
    try {
      const { data }: AxiosResponse = await API.getSubLocation(groupId, locationId, subId);
      commit('GET_SUB_LOCATION_NAME', data);
    } catch (e) {
      throw e;
    }
  },
  async setSubLocation(
    { commit },
    {
      groupId,
      locationId,
      payload,
    }: { groupId: string; locationId: string; payload: c_Interface.CreateLocationPayload }
  ) {
    try {
      const { data }: AxiosResponse = await API.setSubLocation(groupId, locationId, payload);
      return data;
    } catch (e) {
      throw e;
    }
  },
  async updateSubLocation(
    { commit },
    {
      groupId,
      locationId,
      subId,
      payload,
    }: {
      groupId: string;
      locationId: string;
      subId: string;
      payload: c_Interface.CreateLocationPayload;
    }
  ) {
    try {
      const { data }: AxiosResponse = await API.updateSubLocation(
        groupId,
        locationId,
        subId,
        payload
      );
    } catch (e) {
      throw e;
    }
  },
  async deleteSubLocation(
    { commit },
    { groupId, locationId, subId }: { groupId: string; locationId: string; subId: string }
  ) {
    try {
      const { data }: AxiosResponse = await API.deleteSubLocation(groupId, locationId, subId);
    } catch (e) {
      throw e;
    }
  },
  async getTypes({ commit }, groupId: string) {
    try {
      const { data }: AxiosResponse = await API.getTypes(groupId);
      commit('GET_TYPES', data);
    } catch (e) {
      throw e;
    }
  },
  async setType(
    { commit },
    { groupId, payload }: { groupId: string; payload: c_Interface.CustomFieldPayload }
  ) {
    try {
      const { data }: AxiosResponse = await API.setType(groupId, payload);
      return data;
    } catch (e) {
      throw e;
    }
  },
  async deleteType({ commit }, { groupId, typeId }: { groupId: string; typeId: string }) {
    try {
      const { data }: AxiosResponse = await API.deleteType(groupId, typeId);
    } catch (e) {
      throw e;
    }
  },
  async updateType(
    { commit },
    { groupId, typeId, payload }: { groupId: string; typeId: string; payload: { name: string } }
  ) {
    try {
      const { data }: AxiosResponse = await API.updateType(groupId, typeId, payload);
    } catch (e) {
      throw e;
    }
  },
  async getTypeFields({ commit }, { groupId, typeId }: { groupId: string; typeId: string }) {
    try {
      const { data }: AxiosResponse = await API.getTypeFields(groupId, typeId);
      commit('GET_TYPE_FIELDS', data);
    } catch (e) {
      throw e;
    }
  },
  async getTypeField(
    { commit },
    { groupId, typeId, fieldId }: { groupId: string; typeId: string; fieldId: string }
  ) {
    try {
      const { data }: AxiosResponse = await API.getTypeField(groupId, typeId, fieldId);
      commit('GET_TYPE_FIELD_ITEM', data);
    } catch (e) {
      throw e;
    }
  },
  async setTypeField(
    { commit },
    {
      groupId,
      typeId,
      payload,
    }: { groupId: string; typeId: string; payload: c_Interface.CustomFieldPayload }
  ) {
    try {
      const { data }: AxiosResponse = await API.setTypeField(groupId, typeId, payload);
    } catch (e) {
      throw e;
    }
  },
  async updateTypeField(
    { commit },
    {
      groupId,
      typeId,
      fieldId,
      payload,
    }: { groupId: string; typeId: string; fieldId: string; payload: c_Interface.CustomFieldPayload }
  ) {
    try {
      const { data }: AxiosResponse = await API.updateTypeField(groupId, typeId, fieldId, payload);
    } catch (e) {
      throw e;
    }
  },
  async deleteTypeField(
    { commit },
    { groupId, typeId, fieldId }: { groupId: string; typeId: string; fieldId: string }
  ) {
    try {
      const { data }: AxiosResponse = await API.deleteTypeField(groupId, typeId, fieldId);
    } catch (e) {
      throw e;
    }
  },
  async getVendors({ commit }, groupId: string) {
    try {
      const { data }: AxiosResponse = await API.getVendors(groupId);
      commit('GET_VENDORS', data);
    } catch (e) {
      throw e;
    }
  },
  async setVendor(
    { commit },
    { groupId, payload }: { groupId: string; payload: c_Interface.CreateVendorPayload }
  ) {
    try {
      const { data }: AxiosResponse = await API.setVendor(groupId, payload);
      return data;
    } catch (e) {
      throw e;
    }
  },
  async getVendor({ commit }, { groupId, vendorId }: { groupId: string; vendorId: string }) {
    try {
      const { data }: AxiosResponse = await API.getVendor(groupId, vendorId);
      commit('GET_VENDOR_ITEM', data);
    } catch (e) {
      throw e;
    }
  },
  async updateVendor(
    { commit },
    { groupId, vendorId, payload }: { groupId: string; vendorId: string; payload: VendorForm }
  ) {
    try {
      const { data }: AxiosResponse = await API.updateVendor(groupId, vendorId, payload);
    } catch (e) {
      throw e;
    }
  },
  async deleteVendor({ commit }, { groupId, vendorId }: { groupId: string; vendorId: string }) {
    try {
      const { data }: AxiosResponse = await API.deleteVendor(groupId, vendorId);
    } catch (e) {
      throw e;
    }
  },
  async getManufacturers({ commit }, groupId: string) {
    try {
      const { data }: AxiosResponse = await API.getManufacturers(groupId);
      commit('GET_MANUFACTURERS', data);
    } catch (e) {
      throw e;
    }
  },
  async setManufacturer(
    { commit },
    { groupId, payload }: { groupId: string; payload: c_Interface.CreateManufacturerPayload }
  ) {
    try {
      const { data }: AxiosResponse = await API.setManufacturer(groupId, payload);
      return data;
    } catch (e) {
      throw e;
    }
  },
  async getManufacturer(
    { commit },
    { groupId, manufacturerId }: { groupId: string; manufacturerId: string }
  ) {
    try {
      const { data }: AxiosResponse = await API.getManufacturer(groupId, manufacturerId);
      commit('GET_MANUFACTURER_ITEM', data);
    } catch (e) {
      throw e;
    }
  },
  async updateManufacturer(
    { commit },
    {
      groupId,
      manufacturerId,
      payload,
    }: { groupId: string; manufacturerId: string; payload: ManufacturerForm }
  ) {
    try {
      const { data }: AxiosResponse = await API.updateManufacturer(
        groupId,
        manufacturerId,
        payload
      );
    } catch (e) {
      throw e;
    }
  },
  async deleteManufacturer(
    { commit },
    { groupId, manufacturerId }: { groupId: string; manufacturerId: string }
  ) {
    try {
      const { data }: AxiosResponse = await API.deleteManufacturer(groupId, manufacturerId);
    } catch (e) {
      throw e;
    }
  },
  async getAddresses({ commit }, groupId: string) {
    try {
      const { data }: AxiosResponse = await API.getAddresses(groupId);
      commit('GET_ADDRESSES', data);
    } catch (e) {
      throw e;
    }
  },
  async setAddress(
    { commit },
    { groupId, payload }: { groupId: string; payload: c_Interface.CreateAddressPayload }
  ) {
    try {
      const { data }: AxiosResponse = await API.setAddress(groupId, payload);
      return data;
    } catch (e) {
      throw e;
    }
  },
  async getAddress({ commit }, { groupId, addressId }: { groupId: string; addressId: string }) {
    try {
      const { data }: AxiosResponse = await API.getAddress(groupId, addressId);
      commit('GET_ADDRESS_ITEM', data);
    } catch (e) {
      throw e;
    }
  },
  async updateAddress(
    { commit },
    { groupId, addressId, payload }: { groupId: string; addressId: string; payload: AddressForm }
  ) {
    try {
      const { data }: AxiosResponse = await API.updateAddress(groupId, addressId, payload);
    } catch (e) {
      throw e;
    }
  },
  async deleteAddress({ commit }, { groupId, addressId }: { groupId: string; addressId: string }) {
    try {
      const { data }: AxiosResponse = await API.deleteAddress(groupId, addressId);
    } catch (e) {
      throw e;
    }
  },
  async getGrants({ commit }, groupId: string) {
    try {
      const { data }: AxiosResponse = await API.getGrants(groupId);
      commit('GET_GRANTS', data);
    } catch (e) {
      throw e;
    }
  },
  async setGrant(
    { commit },
    { groupId, payload }: { groupId: string; payload: c_Interface.CreateGrantPayload }
  ) {
    try {
      const { data }: AxiosResponse = await API.setGrant(groupId, payload);
      return data;
    } catch (e) {
      throw e;
    }
  },
  async getGrant({ commit }, { groupId, grantId }: { groupId: string; grantId: string }) {
    try {
      const { data }: AxiosResponse = await API.getGrant(groupId, grantId);
      commit('GET_GRANT_ITEM', data);
    } catch (e) {
      throw e;
    }
  },
  async updateGrant(
    { commit },
    { groupId, grantId, payload }: { groupId: string; grantId: string; payload: GrantForm }
  ) {
    try {
      const { data }: AxiosResponse = await API.updateGrant(groupId, grantId, payload);
    } catch (e) {
      throw e;
    }
  },
  async deleteGrant({ commit }, { groupId, grantId }: { groupId: string; grantId: string }) {
    try {
      const { data }: AxiosResponse = await API.deleteGrant(groupId, grantId);
    } catch (e) {
      throw e;
    }
  },
  async getSettings({ commit }, groupId: string) {
    try {
      const { data }: AxiosResponse = await API.getSettings(groupId);
      commit('GET_SETTINGS', data);
    } catch (e) {
      throw e;
    }
  },
  async updateSettings(
    { commit },
    { groupId, payload }: { groupId: string; payload: c_Interface.SettingPayload }
  ) {
    try {
      const { data }: AxiosResponse = await API.updateSetting(groupId, payload);
    } catch (e) {
      throw e;
    }
  },
};
